import React from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@racoon/ui";
import { ChevronDown } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";

type Props = {
  /** How many rows are checked — drives the badge and the disabled state */
  selectedCount: number;
  onCancelJobs: () => void;
  onEditJob?: () => void;
  /** Editing opens a single-job dialog, so it needs exactly one checked row */
  canEditJob?: boolean;
  /** Adds the checked jobs to a trip that already exists (target picked in the modal) */
  onAddJobsToRoute?: () => void;
  /**
   * A trip takes one СО job at a time, so a multi-СО selection has no target —
   * and every checked job has to still be in "Создано".
   */
  canAddJobsToRoute?: boolean;
  /** Opens "assign route" with the checked jobs already in its first trip */
  onCreateRoute?: () => void;
  /** Routing an already-assigned job is not possible, so it needs "Создано" rows only */
  canCreateRoute?: boolean;
};

/**
 * "Действие" dropdown for the orders table — acts on the rows checked in the
 * leading checkbox column.
 */
export const OrdersActionsMenu: React.FC<Props> = ({
  selectedCount,
  onCancelJobs,
  onEditJob,
  canEditJob = false,
  onAddJobsToRoute,
  canAddJobsToRoute = true,
  onCreateRoute,
  canCreateRoute = true,
}) => {
  const t = useT();
  const disabled = selectedCount === 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id="orders-actions-button"
          size="sm"
          variant="opacityBlueWithoutOutline"
          disabled={disabled}
          className="gap-1.5"
        >
          Действие
          {selectedCount > 0 && (
            <span className="text-xxs px-1 min-w-4 h-4 font-medium rounded-full bg-brand-blue text-white flex items-center justify-center">
              {selectedCount}
            </span>
          )}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuItem disabled={!canEditJob} onSelect={() => canEditJob && onEditJob?.()}>
          {t(Namespace.Orders, "actions.edit")}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!onAddJobsToRoute || !canAddJobsToRoute}
          onSelect={() => canAddJobsToRoute && onAddJobsToRoute?.()}
        >
          {t(Namespace.Routes, "addJobsToRoute")}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!onCreateRoute || !canCreateRoute}
          onSelect={() => canCreateRoute && onCreateRoute?.()}
        >
          {t(Namespace.Routes, "createNewRoute")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onCancelJobs} className="text-brand-red">
          Отменить задания
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
